import { isPlatformBrowser } from '@angular/common';
import {
    Directive,
    effect,
    inject,
    input,
    NgZone,
    OnDestroy,
    PLATFORM_ID,
} from '@angular/core';

@Directive({
    selector: '[dynamicHeaderPage]',
    standalone: true,
})
export class DynamicHeaderPageDirective implements OnDestroy {
    pageDataLoaded = input<boolean>(false);
    dynamicHeaderPage = input<boolean>(false);
    excludedHeaders = input<string[]>([]);

    private readonly platformId = inject(PLATFORM_ID);
    private readonly isBrowser = isPlatformBrowser(this.platformId);
    private readonly ngZone = inject(NgZone);

    private boundScrollHandler?: () => void;
    private boundVisibilityHandler?: () => void;
    private boundPageShowHandler?: (event: PageTransitionEvent) => void;
    private boundFocusHandler?: () => void;
    private modeResolved = false;
    private lastMode: boolean | null = null;
    private appliedOnce = false;

    constructor() {
        if (!this.isBrowser) return;

        this.initBaseScroll();
        effect(() => {
            const isDynamic = this.dynamicHeaderPage();
            const loaded = this.pageDataLoaded();

            if (!loaded) return;

            // permite re-ejecutar en primera carga aunque el valor no cambie
            if (this.appliedOnce && this.lastMode === isDynamic) return;

            this.lastMode = isDynamic;
            this.appliedOnce = true;
            this.modeResolved = true;

            this.ensureApplied(isDynamic);
        });
    }

    ngOnDestroy(): void {
        if (!this.isBrowser) return;

        if (this.boundScrollHandler) {
            document.removeEventListener(
                'scroll',
                this.boundScrollHandler,
                true
            );
            window.removeEventListener('wheel', this.boundScrollHandler);
        }

        if (this.boundVisibilityHandler) {
            document.removeEventListener(
                'visibilitychange',
                this.boundVisibilityHandler
            );
        }

        if (this.boundPageShowHandler) {
            window.removeEventListener('pageshow', this.boundPageShowHandler);
        }

        if (this.boundFocusHandler) {
            window.removeEventListener('focus', this.boundFocusHandler);
        }
    }

    private ensureApplied(isDynamic: boolean) {
        let attempts = 0;

        const tryApply = () => {
            const header = document.querySelector('.fixed_header');
            const container = document.querySelector(
                '.header-desktop-container'
            );

            // CLAVE: Si la pestaña está en segundo plano, offsetHeight será 0.
            // Permitimos que continúe si document.hidden es true.
            const isHidden =
                document.hidden || document.visibilityState !== 'visible';
            const isReady =
                header &&
                container &&
                ((container as HTMLElement).offsetHeight > 0 || isHidden);

            if (isReady) {
                this.ngZone.run(() => {
                    this.applyMode(isDynamic);
                });

                // RE-APLICACIÓN FINAL (esto arregla el 1st paint bug)
                setTimeout(() => {
                    this.applyMode(this.dynamicHeaderPage());
                }, 0);

                return;
            }

            if (attempts < 50) {
                // Aumentamos de 20 a 50 intentos
                attempts++;
                requestAnimationFrame(tryApply);
            } else if (header && container) {
                // Fallback de seguridad: si se acaban los intentos pero los elementos existen, aplicamos.
                this.ngZone.run(() => {
                    this.applyMode(isDynamic);
                });
            }
        };

        requestAnimationFrame(tryApply);
    }

    private initBaseScroll(): void {
        const hasTopSlider = this.hasTopSlider();
        const refreshHeaderState = () => {
            this.ngZone.run(() => {
                if (this.modeResolved) {
                    this.applyMode(this.dynamicHeaderPage());
                }
            });
        };

        this.boundScrollHandler = () => {
            this.ngZone.run(() => {
                if (this.modeResolved) {
                    this.manageScroll(hasTopSlider);
                }
            });
        };

        document.addEventListener('scroll', this.boundScrollHandler, true);
        window.addEventListener('wheel', this.boundScrollHandler, {
            passive: true,
        });
        this.boundVisibilityHandler = () => {
            if (document.visibilityState === 'visible') {
                refreshHeaderState();
            }
        };
        this.boundPageShowHandler = (event: PageTransitionEvent) => {
            if (event.persisted) {
                refreshHeaderState();
            }
        };
        this.boundFocusHandler = () => {
            refreshHeaderState();
        };

        document.addEventListener(
            'visibilitychange',
            this.boundVisibilityHandler
        );
        window.addEventListener('pageshow', this.boundPageShowHandler);
        window.addEventListener('focus', this.boundFocusHandler);

        effect(() => {
            const isDynamic = this.dynamicHeaderPage();

            if (this.lastMode === isDynamic) return;

            this.lastMode = isDynamic;
            this.modeResolved = true;

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.ngZone.run(() => {
                        this.applyMode(isDynamic);
                    });
                });
            });
        });
    }

    private applyMode(isDynamic: boolean): void {
        const hasTopSlider = this.hasTopSlider();

        // La cabecera es superpuesta y no debe reservar espacio vertical.
        // Se fuerza también desde JS para corregir HTML SSR o CSS cacheado
        // que pueda conservar el antiguo padding-top de 80px.
        document.body.style.setProperty('margin-top', '0px', 'important');
        document.body.style.setProperty('padding-top', '0px', 'important');
        document.querySelectorAll('.main-cotainer').forEach((element) => {
            const main = element as HTMLElement;
            main.style.setProperty('margin-top', '0px', 'important');
            main.style.setProperty('padding-top', '0px', 'important');
        });

        // RESET
        document.body.classList.remove('header_sticky');

        document.querySelectorAll('.fixed_header')?.forEach((el) => {
            el.classList.remove('header-sticky', 'cs_gescout_sticky');
            (el as HTMLElement).style.backgroundColor = '';
            (el as HTMLElement).style.opacity = '';
        });

        const header = document.querySelector(
            '.header-desktop-container'
        ) as HTMLElement;

        if (header) {
            header.style.opacity = '';
            header.style.backgroundColor = '';
        }

        this.applyHeaderClasses();

        if (isDynamic) {
            this.initDynamicMode(hasTopSlider);
        } else {
            this.initStaticMode();
        }

        this.manageScroll(hasTopSlider);
    }

    private initDynamicMode(hasTopSlider: boolean): void {
        this.manageScroll(hasTopSlider);
    }

    private initStaticMode(): void {
        document.body.classList.add('header_sticky');

        document.querySelectorAll('.fixed_header')?.forEach((el) => {
            el.classList.add('header-sticky', 'cs_gescout_sticky');
        });

        const header = document.querySelector(
            '.header-desktop-container'
        ) as HTMLElement;

        if (header) {
            header.style.opacity = '1';
            header.style.backgroundColor = '#ffffff';
        }
    }

    private manageScroll(hasTopSlider: boolean): void {
        if (!this.modeResolved) return;

        const isDynamic = this.dynamicHeaderPage();

        const threshold = isDynamic ? 100 : 0;
        const scrollY =
            window.scrollY || document.documentElement.scrollTop || 0;

        if (scrollY >= threshold) {
            document.body.classList.add('header_sticky');

            document
                .querySelectorAll('.fixed_header')
                ?.forEach((el) =>
                    el.classList.add('header-sticky', 'cs_gescout_sticky')
                );
        } else {
            if (isDynamic) {
                document.body.classList.remove('header_sticky');

                document
                    .querySelectorAll('.fixed_header')
                    ?.forEach((el) =>
                        el.classList.remove(
                            'header-sticky',
                            'cs_gescout_sticky'
                        )
                    );
            }
        }

        if (window.innerWidth >= 1024) {
            const el = document.getElementById('babylon__gallery-slider');
            const position = (el?.offsetTop ?? 200) - 200;

            const gallery = document.querySelector(
                '.babylon__gallery-slider .babylon__gallery-slider--content'
            );

            if (!gallery) return;

            window.scrollY >= position
                ? gallery.classList.remove('no-active')
                : gallery.classList.add('no-active');
        }
    }

    private hasTopSlider(): boolean {
        return (
            !!document.querySelector('.babylon__top-slider') ||
            !!document.querySelector('lib-babylon-top-slider')
        );
    }

    private applyHeaderClasses(): void {
        if (document.querySelector('#headerCenterBookShow')) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('header-center-bookshow-engine');
        }

        if (document.querySelector('#headerBookShow')) {
            document
                .querySelector('.babylon__engine')
                ?.classList.add('header-bookshow-engine');
        }
    }
}
